/**
 * useTable Hook
 * Manages table state: pagination, sorting, filtering, and search.
 * 
 * Usage:
 *   const { 
 *     items, 
 *     pagination, 
 *     search, 
 *     setSearch,
 *     sort,
 *     setSort,
 *     filters,
 *     setFilter
 *   } = useTable(data, { 
 *     pageSize: 20,
 *     searchFields: ['name', 'email'],
 *     defaultSort: { field: 'created_at', order: 'desc' }
 *   });
 */
import { useState, useMemo, useCallback } from 'react';

export function useTable(data = [], options = {}) {
  const {
    pageSize = 20,
    searchFields = [],
    defaultSort = { field: 'created_at', order: 'desc' },
    defaultFilters = {}
  } = options;

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort);
  const [filters, setFilters] = useState(defaultFilters);

  // Filter helper
  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(1); // Reset to first page on filter change
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setSearch('');
    setPage(1);
  }, [defaultFilters]);

  // Filtered and sorted data
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (search && searchFields.length > 0) {
      const searchLower = search.toLowerCase();
      result = result.filter(item =>
        searchFields.some(field => {
          const value = getNestedValue(item, field);
          return value && String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        result = result.filter(item => {
          const itemValue = getNestedValue(item, key);
          if (Array.isArray(value)) {
            return value.includes(itemValue);
          }
          return itemValue === value;
        });
      }
    });

    // Apply sorting
    if (sort.field) {
      result.sort((a, b) => {
        const aVal = getNestedValue(a, sort.field);
        const bVal = getNestedValue(b, sort.field);

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return sort.order === 'desc' ? -comparison : comparison;
      });
    }

    return result;
  }, [data, search, searchFields, filters, sort]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, page, pageSize]);

  // Pagination controls
  const pagination = {
    page,
    pageSize,
    totalPages,
    totalItems: processedData.length,
    hasNext: page < totalPages,
    hasPrev: page > 1,
    goToPage: (p) => setPage(Math.max(1, Math.min(p, totalPages))),
    onPageChange: (p) => setPage(Math.max(1, Math.min(p, totalPages))), // Alias for Pagination component
    nextPage: () => setPage(p => Math.min(p + 1, totalPages)),
    prevPage: () => setPage(p => Math.max(p - 1, 1)),
    firstPage: () => setPage(1),
    lastPage: () => setPage(totalPages)
  };

  // Sort handler
  const handleSort = useCallback((field) => {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  return {
    // Data
    items: paginatedData,
    allItems: processedData,
    
    // Search
    search,
    setSearch: (value) => {
      setSearch(value);
      setPage(1);
    },
    
    // Sort
    sort,
    setSort: handleSort,
    
    // Filters
    filters,
    setFilter,
    clearFilters,
    
    // Pagination
    pagination,
    
    // Helpers
    isEmpty: processedData.length === 0,
    isFiltered: search || Object.values(filters).some(v => v !== undefined && v !== null && v !== '')
  };
}

// Helper to get nested object values (e.g., 'user.name')
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export default useTable;
